import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Input } from '@Sage/hoi-poi-ui';

import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

import './styles/text.scss';

const propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    description: PropTypes.string,
    error: PropTypes.any,
    fieldId: PropTypes.string,
    forceFocus: PropTypes.bool,
    hasExternalLabel: PropTypes.bool,
    hidden: PropTypes.bool,
    hint: PropTypes.string,
    isSafeNumber: PropTypes.bool,
    label: PropTypes.any,
    labelMode: PropTypes.string,
    mandatory: PropTypes.bool,
    maxLength: PropTypes.number,
    onChange: PropTypes.func,
    onEnter: PropTypes.func,
    onInput: PropTypes.func,
    placeholderReadOnly: PropTypes.string,
    readOnly: PropTypes.bool,
    shouldRenderField: PropTypes.func,
    size: PropTypes.oneOf(['small', 'medium', 'large', 'xlarge']),
    type: PropTypes.string,
    value: PropTypes.string,
};

const Text = ({
    autoFocus,
    className,
    description,
    error,
    fieldId,
    forceFocus,
    hasExternalLabel,
    hidden,
    hint,
    isFullWidth = true,
    isSafeNumber,
    label,
    labelMode,
    mandatory,
    maxLength,
    onChange,
    onEnter,
    onInput,
    placeholderReadOnly,
    readOnly,
    shouldRenderField,
    size,
    type = 'text',
    value = '',
}) => {
    const [text, setText] = useState(value);
    const prevValue = useRef(value);

    useEffect(() => {
        if (value !== text && prevValue.current !== value) setText(value);
        prevValue.current = value;
    }, [text, value]);

    const rootClassName = useMemo(
        () =>
            classnames('fm-field-textinput-v2', className, {
                'fm-field-textinput-v2--large': size === 'large',
                'fm-field-textinput-v2--xlarge': size === 'xlarge',
            }),
        [className, size],
    );

    // Real-time maxLength validation
    const errorLength = useMemo(() => {
        if (maxLength && maxLength > 0 && maxLength !== -1) {
            const strVal = text || '';
            if (strVal.length > maxLength) {
                return getLiteralWithParameters('error_text_too_long', [maxLength]);
            }
        }
        return '';
    }, [text, maxLength]);

    const onInnerChange = useCallback(
        (newValue, _, info) => {
            // Limit number inputs to safe integer (9 chars max).
            // Backend expects int32, so we can't exceed this limit.
            if (isSafeNumber && newValue && newValue.length > 10) return;

            setText(newValue);
            onInput && onInput(newValue);
            if (info?.action === 'clear' && onChange) onChange(newValue);
        },
        [isSafeNumber, onChange, onInput],
    );

    const onBlur = useCallback(() => {
        onChange && onChange(text);
    }, [onChange, text]);

    const handleEnter = useCallback(
        (finalValue, e) => {
            const nextValue = finalValue ?? text;
            setText(nextValue);
            onChange && onChange(nextValue);
            onEnter && onEnter(finalValue, e);
        },
        [onChange, onEnter, text],
    );

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;

    // External error takes priority to avoid duplication with FieldWrapper
    // Internal error (errorLength) only shows when no external error
    const finalError = error || errorLength;

    const placeholder = hint || getLiteral('placeholder_text_field');
    const resolvedPlaceholder =
        mandatory && !label && !hasExternalLabel ? `${placeholder} *` : placeholder;

    return (
        <Input
            autoFocus={autoFocus || forceFocus}
            className={rootClassName}
            error={finalError}
            hint={description}
            isFullWidth={isFullWidth}
            isReadOnly={readOnly}
            isRequired={mandatory}
            label={label}
            labelMode={labelMode}
            onBlur={onBlur}
            onChange={onInnerChange}
            onEnter={handleEnter}
            placeholder={resolvedPlaceholder}
            placeholderReadOnly={placeholderReadOnly || resolvedPlaceholder}
            type={type}
            value={text}
        />
    );
};

Text.displayName = 'TextField';
Text.propTypes = propTypes;

export default memo(Text);
